/**
 * Text for the shared-server registry (contract R3-D): real servers may have a
 * blank size, no IP / FQDN yet and no tracked capacity (capacity_limit null).
 *
 * Node-safe: relative imports only, no import.meta.
 */
import type { SharedServer, SharedUnavailableReason } from '../../types/workwise'

const REASON_LABEL: Record<SharedUnavailableReason, string> = {
  degraded: 'degraded',
  offline: 'offline',
  maintenance: 'maintenance',
  full: 'full',
  over_threshold: 'near capacity',
}

type Capacity = Pick<SharedServer, 'capacity_limit' | 'tenant_count' | 'capacity_pct'>

export function isCapacityTracked(server: Capacity): boolean {
  return server.capacity_limit !== null && server.capacity_limit !== undefined
}

/** "6/20 tenants (30%)", or "capacity not tracked" when the registry has no limit. */
export function capacityText(server: Capacity): string {
  if (!isCapacityTracked(server)) return 'capacity not tracked'
  return `${server.tenant_count}/${server.capacity_limit} tenants (${server.capacity_pct ?? 0}%)`
}

/** "VM running" for a server read live from Azure (staging), else "". */
function liveState(server: SharedServer): string {
  return server.live && server.power_state ? `VM ${server.power_state}` : ''
}

/**
 * Dropdown label, blank parts left out, e.g.
 *   "sqlsh-dev-eastus2-01 · E16s_v5 · 6/20 tenants (30%) · healthy"
 *   "PMTWWDB01 · capacity not tracked · healthy"
 *   "dwwstgdb01 · Standard_D4s_v5 · 2/20 tenants (10%) · healthy · VM running" (staging, live from Azure)
 * plus " — unavailable: <reason>" for a server that cannot be picked.
 */
export function sharedServerLabel(server: SharedServer): string {
  const parts = [server.name, (server.size || '').trim(), capacityText(server), server.status, liveState(server)].filter(Boolean)
  const reason = server.selectable
    ? ''
    : ` — unavailable: ${server.unavailable_reason ? REASON_LABEL[server.unavailable_reason] : 'not selectable'}`
  return `${parts.join(' · ')}${reason}`
}

/** "E16s_v5, 6/20 tenants" for the summary; "capacity not tracked" when there is nothing else. */
export function sharedServerDetail(server: SharedServer): string {
  const capacity = isCapacityTracked(server) ? `${server.tenant_count}/${server.capacity_limit} tenants` : 'capacity not tracked'
  return [(server.size || '').trim(), capacity].filter(Boolean).join(', ')
}

/** The hint under the select: address (when recorded) and what attaching does to the capacity. */
export function sharedServerHint(server: SharedServer): string {
  const address = [server.fqdn, server.private_ip].map((v) => (v || '').trim()).filter(Boolean).join(' · ')
  const capacity = isCapacityTracked(server)
    ? `${server.tenant_count} of ${server.capacity_limit} tenants in use; this customer adds one.`
    : 'Capacity is not tracked for this server; this customer adds one tenant.'
  const base = address ? `${address} · ${capacity}` : capacity
  if (!server.live) return base
  const state = server.power_state ? ` (VM ${server.power_state})` : ''
  return `${base} Size, address and network were read live from Azure${state}.`
}
